import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Flex, Text } from 'onefaceui-react-components';
import { getResourceTo } from 'react/utils/utils';
import { Container } from './styles';

function DetailedTooltip({
  progressNumbers,
  display,
  clientX,
  clientY
}) {
  const leftPosition = `${clientX > 115 ? clientX - 60 : 55}px`;
  const topPosition = `${clientY - 15}px`;
  const toTopClass = clientY > 692 ? 'toTop' : '';

  const stopStatusTypesList = useMemo(() => {
    const {
      done,
      canceled,
      redelivery,
      undelivery,
      partial,
      progressCanceled,
      progressUndelivered,
      progressRedelivered,
      progressDelivered,
      progressPartial
    } = progressNumbers;

    const progressCanceledText = `${canceled} (${progressCanceled})`;
    const progressUndeliveredText = `${undelivery} (${progressUndelivered})`;
    const progressRedeliveredText = `${redelivery} (${progressRedelivered})`;
    const progressDeliveredText = `${done} (${progressDelivered})`;
    const progressPartialText = `${partial || 0} (${progressPartial ?? '0%'})`;

    return [
      {
        type: 'delivered-stop',
        name: getResourceTo('RouteMonitor.sidebar.stopsStatistics.Delivered'),
        values: progressDeliveredText
      },
      {
        type: 'partial-delivered',
        name: getResourceTo('Survey.ApplyRules.Order.partialDelivery'),
        values: progressPartialText
      },
      {
        type: 'redelivered-stop',
        name: getResourceTo('Greenmile.Stop.Undeliverable.btnSaveRedelivery'),
        values: progressRedeliveredText
      },
      {
        type: 'undelived-stop',
        name: getResourceTo('Greenmile.Stop.IconDescription.undeliverable'),
        values: progressUndeliveredText
      },
      {
        type: 'canceled-stop',
        name: getResourceTo('RouteProgress.StopStatus.Canceled'),
        values: progressCanceledText
      }
    ];
  }, [progressNumbers]);

  const stopsTotalsList = useMemo(() => {
    const {
      total,
      redelivery,
      cloned,
      done,
      canceled,
      undelivery,
      partial,
      planned
    } = progressNumbers;

    const totalServiced = done + partial + redelivery + undelivery + canceled;

    return [
      {
        type: 'planned-stops',
        name: getResourceTo('RouteMonitor.sidebar.PlannedStops'),
        value: planned
      },
      {
        type: 'redelivery-stops',
        name: '',
        value: `+${redelivery} ${getResourceTo('Greenmile.Stop.Undeliverable.btnSaveRedelivery')}`,
        shouldShow: redelivery > 0,
        weight: 'light'
      },
      {
        type: 'cloned-stops',
        name: '',
        value: `+${cloned} ${getResourceTo('RouteMonitor.sidebar.Cloned')}`,
        shouldShow: cloned > 0,
        weight: 'light'
      },
      {
        type: 'serviced-stops',
        name: getResourceTo('RouteMonitor.sidebar.StopsServiced'),
        value: `${totalServiced || 0}/${planned || 0}`
      },
      {
        type: 'total-route-stops',
        name: getResourceTo('RouteMonitor.sidebar.TotalRouteStops'),
        value: total
      }
    ];
  }, [progressNumbers]);

  return (
    <Container
      gap={8}
      data-testid={display === 'flex' ? 'tooltip-showing' : 'tooltip-hidden'}
      className={toTopClass}
      style={{
        display,
        left: leftPosition,
        top: topPosition
      }}
    >
      <div>
        {stopStatusTypesList.map(({ type, values, name }) => (
          <Flex key={type} justifyContent="space-between" className="tooltipLine">
            <Flex gap={4} alignItems="center">
              <span className={`tooltipColor ${type}`} />
              <Text size="xs" data-testid={`${type}-name`}>{name}</Text>
            </Flex>
            <Text
              size="xs"
              data-testid={`${type}-text`}
            >
              {values}
            </Text>
          </Flex>
        ))}
      </div>
      <div>
        {stopsTotalsList.map(({
          name, type, value, shouldShow, weight = 'bold'
        }) => (
          <Flex
            key={type}
            justifyContent="space-between"
            shouldShow={shouldShow}
          >
            <Text weight={weight} size="xs" data-testid={`${type}-name`}>
              {name}
            </Text>
            <Text weight={weight} size="xs" data-testid={`${type}-text`}>
              {value}
            </Text>
          </Flex>
        ))}
      </div>
      <span className="tooltipTriangle" data-testid="tooltip-triangle">
        <svg
          width="16"
          height="8"
          viewBox="0 0 30 10"
          preserveAspectRatio="none"
        >
          <polygon points="0,0 30,0 15,10" />
        </svg>
      </span>
    </Container>
  );
}

DetailedTooltip.propTypes = {
  progressNumbers: PropTypes.object.isRequired,
  display: PropTypes.string.isRequired,
  clientX: PropTypes.number.isRequired,
  clientY: PropTypes.number.isRequired
};

export default DetailedTooltip;
